import React, { useState, useEffect } from 'react';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { BASE_URL, getAxiosConfig } from '../../../components/interface/Config';
import LoadingAnimation from '../../../components/LoadingAnimation';
import { apiHandler } from '../../../services/authService';


interface Answer {
  id: number;
  name: string;
}

interface VisionScaleFormProps {
  patientId: string;
}

interface Question {
  id: number;
  name: string;
  answers: Answer[];
}

interface Form {
  id: number;
  name: string;
  questions: {
    question: Question;
    answers: {
      answer: Answer;
    }[];
  }[];
  latest_form_submisson?: {
    form_id: number;
    patient_id: number;
    answers: {
      [key: number]: {
        id: number;
        name: string;
        score: number;
        is_active: number;
        created_at: string | null;
        updated_at: string | null;
      };
    };
    score: string;
    created_at: string;
    message: string;
  };
}

const VisionScaleForm: React.FC<VisionScaleFormProps> = ({ patientId }) => {

  const [form, setForm] = useState<Form | null>(null);
  const [selectedAnswers, setSelectedAnswers] = useState<{ [key: number]: number }>({});
  const [isReadOnly, setIsReadOnly] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  // Snackbar states
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'info' | 'warning' | 'error'>('info');

  useEffect(() => {
    fetchFormData();
  }, [patientId]);

  const fetchFormData = async () => {
    setIsLoading(true);
    const url = `${BASE_URL}/resource/form?limit=20&slug=vision&page=1&searchkey=&order_by=order&dir=1&user_id=${patientId}`;
    await apiHandler('', 'GET', {}, url)
      .then(response => {
        if (!response) {
          throw new Error("No response received from the server");
        }
       const data = response.data; 
        console.log('fetchFormData data',data)
        if (data && data.data) {
          const formData = data?.data?.forms?.find((f: Form) => f.id === 29);
          console.log('formData', formData);
          if (formData) {
            setForm(formData);
  
            if (formData.latest_form_submisson) {
              const answers = formData.latest_form_submisson.answers;
              setSelectedAnswers(Object.keys(answers).reduce((acc, key) => {
                acc[parseInt(key)] = answers[parseInt(key)].id;
                return acc;
              }, {} as { [key: number]: number }));
            }
          }
        }
      })
      .catch(error => {
        console.error('Error fetching form data:', error);
        setSnackbarMessage('Error fetching form data.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      })
      .finally(() => setIsLoading(false));

    // fetch(`${BASE_URL}/resource/form?limit=20&slug=vision&page=1&searchkey=&order_by=order&dir=1&user_id=${patientId}`, {
    //   headers: getAxiosConfig().headers
    // })
    //   .then(response => response.json())
    //   .then(data => {
    //     const formData = data.data.forms.find((f: Form) => f.id === 29);
    //     if (formData) {
    //       setForm(formData);
    //       if (formData.latest_form_submisson) {
    //         const answers = formData.latest_form_submisson.answers;
    //         setSelectedAnswers(Object.keys(answers).reduce((acc, key) => {
    //           acc[parseInt(key)] = answers[parseInt(key)].id;
    //           return acc;
    //         }, {} as { [key: number]: number }));
    //       }
    //     }
    //   })
    //   .catch(error => {
    //     console.error('Error fetching form data:', error);
    //     setSnackbarMessage('Error fetching form data.');
    //     setSnackbarSeverity('error');
    //     setSnackbarOpen(true);
    //   })
    //   .finally(() => setIsLoading(false));
  };

  const handleAnswerSelect = (questionId: number, answerId: number) => {
    setSelectedAnswers((prev) => ({
      ...prev,
      [questionId]: answerId,
    }));
  };

  const handleSubmit = async () => {
    const answers = Object.keys(selectedAnswers).reduce((acc, questionId) => {
      const answerId = selectedAnswers[parseInt(questionId)];
      if (answerId) {
        // Find the answer object corresponding to the selected answer ID
        const selectedAnswer = form!.questions
          .find(q => q.question.id === parseInt(questionId))
          ?.answers.find(a => a.answer.id === answerId);

        // Determine the score based on the answer name
        const score = selectedAnswer?.answer.name === "Yes" ? 1 : 0;

        acc[questionId] = {
          question_id: parseInt(questionId),
          answer_id: answerId,
          jump_to_question_id: null,
          score,
          order: null,
          type: "ordinary",
          label: null,
          answer: {
            id: answerId,
            name: selectedAnswer?.answer.name || "", // Get the name from the selected answer
            is_active: 1,
            created_at: null,
            updated_at: null,
          },
        };
      }
      return acc;
    }, {} as any);

    try {
      const payload = {
        answers,
        form_id: 29,
        form_name: "Vision Symptoms",
        patient_id: patientId,
      }
      const url = `${BASE_URL}/resource/form_submitted_answers`;
      const response = await apiHandler('', 'POST', payload, url);

      if (!response) {
        throw new Error("No response received from the server");
      }

      // const response = await fetch(`${BASE_URL}/resource/form_submitted_answers`, {
      //   method: 'POST',
      //   headers: getAxiosConfig().headers,
      //   body: JSON.stringify({
      //     answers,
      //     form_id: 29,
      //     form_name: "Vision Symptoms",
      //     patient_id: patientId,
      //   }),
      // });

      // if (response.ok) {
      if (response.data.code === 200 || response.data.code === 201) {
        setIsReadOnly(true);
        setSnackbarMessage('Form submitted successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });

        // Fetch the form data again after successful submission
        fetchFormData();
      } else {
        setSnackbarMessage('Error submitting form.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSnackbarMessage('Error submitting form.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleCancel = () => {
    setIsReadOnly(true);
    // If needed, you can also reset selectedAnswers or any other state here.
  };

  if (isLoading) {
    return <LoadingAnimation />;
  }

  if (!form) {
    return <LoadingAnimation />;
  }

  return (
    <div className="bg-gray-100">
      <div className="w-full bg-white px-8 py-4 rounded-lg shadow-lg">
        <div className={`flex mb-4 ${form.latest_form_submisson && form.latest_form_submisson.score !== undefined ? 'justify-between' : 'justify-end'}`}>
          {form.latest_form_submisson && form.latest_form_submisson.score !== undefined && (
            <div className="flex bg-primary p-2 rounded-lg text-white">
              <div className="mr-2">Score:</div>
              <div className="font-semibold">{Number(form.latest_form_submisson.score) > 0 ? form.latest_form_submisson.score : 0}</div>
            </div>
          )}

        </div>

        <div className="flex flex-col gap-6">
          {form.questions.map((item) => (
            <div key={item.question.id} className="w-full mb-2">
              <div className="p-4 border border-gray-300 bg-white rounded-lg shadow-sm h-full flex items-center">
                <div className="flex-grow font-semibold">
                  {item.question.name}
                </div>
                <ToggleButtonGroup
                  value={selectedAnswers[item.question.id] || null}
                  exclusive
                  onChange={(_, newAnswerId) => {
                    if (newAnswerId !== null) {
                      handleAnswerSelect(item.question.id, newAnswerId);
                    }
                  }}
                  aria-label="question answers"
                  disabled={isReadOnly}
                  className="ml-4"
                >
                  {item.answers.map((answerItem) => {
                    const backgroundColor = selectedAnswers[item.question.id] === answerItem.answer.id
                      ? answerItem.answer.name === "Yes"
                        ? '#EE6A6AFF'
                        : '#65AD65FF'
                      : 'transparent';

                    const color = selectedAnswers[item.question.id] === answerItem.answer.id
                      ? 'white'
                      : 'gray';

                    return (
                      <ToggleButton
                        key={answerItem.answer.id}
                        value={answerItem.answer.id}
                        style={{
                          backgroundColor,
                          color: color,
                          width: '96px',
                        }}
                      >
                        {answerItem.answer.name}
                      </ToggleButton>
                    );
                  })}
                </ToggleButtonGroup>
              </div>
            </div>
          ))}
        </div>


      </div>


    </div>
  );
};

export default VisionScaleForm;
